import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Divider, Flex, Form } from 'antd'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useAccountServiceRequestResetPasswordEndpoint,
  useIdentityServiceCreateProfileEndpoint,
  useIdentityServiceGetProfilesListEndpointKey,
  useIdentityServiceUpdateProfileEndpoint
} from 'src/api/queries'
import {
  ApiError,
  GetProfileResponse,
  PersonStatus,
  SystemLanguage,
  UserProfileStatus
} from 'src/api/requests'
import { SaveConfirm, Text, message } from 'src/components'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

import { FormFields } from './FormFields'
import { useStyles } from './styles'

type Props = {
  id?: string
  data?: GetProfileResponse
}

export const UserGeneralCard = ({ id, data }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [form] = Form.useForm()

  const { mutate: create } = useIdentityServiceCreateProfileEndpoint({
    onSuccess: () => onMutationSuccess(),
    onError: (e: ApiError) => onMutationError(e)
  })

  const { mutate: edit } = useIdentityServiceUpdateProfileEndpoint({
    onSuccess: () => onMutationSuccess(),
    onError: (e: ApiError) => onMutationError(e)
  })

  const { mutate: resetPassword } =
    useAccountServiceRequestResetPasswordEndpoint({
      onSuccess: () =>
        message.success(t('auth.reset_password_request_success')),
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const onCancel = () => {
    navigate(paths.users.root)
    form.resetFields()
  }

  const onSave = async () => {
    const values = await form.validateFields()

    const requestBody = {
      ...values,
      dateOfBirth: toDayjs(values.dateOfBirth)?.format(yearMonthDaySlash),
      status: values.status ? PersonStatus.ACTIVE : PersonStatus.INACTIVE
    }

    id
      ? edit({ id, requestBody })
      : create({
          requestBody: { ...requestBody, systemLanguage: SystemLanguage.RO }
        })
  }

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useIdentityServiceGetProfilesListEndpointKey]
    })
    message.success(t('common.save_success'))

    navigate(paths.users.root)
    form.resetFields()
  }

  const onMutationError = (e: ApiError) => {
    form.setFields(formErrorHandler(e.body.errors))
  }

  const onResetPassword = () => {
    const email = data?.email

    if (email) {
      resetPassword({ requestBody: { email } })
    }
  }

  useEffect(() => {
    if (id && data) {
      form.setFieldsValue({
        ...data,
        dateOfBirth: toDayjs(data.dateOfBirth),
        status: data.status === UserProfileStatus.ACTIVE
      })
    }
  }, [data, id])

  return (
    <Form layout="vertical" requiredMark={false} form={form}>
      <Card bordered={false}>
        <Text size="xxl" className={styles.heading}>
          {t('users.general_info')}
        </Text>

        <FormFields
          isDisabled={!!id}
          isEditPage={!!id}
          onResetPassword={onResetPassword}
          form={form}
        />

        <Divider />

        <Flex justify="center" gap={16}>
          <Button onClick={onCancel}>{t('users.cancel')}</Button>

          <SaveConfirm title={`${t('common.save')}?`} onConfirm={onSave}>
            <Button type="primary">{t('common.save')}</Button>
          </SaveConfirm>
        </Flex>
      </Card>
    </Form>
  )
}
